import {getActiveMessages} from '@github-ui/copilot-chat-helpers/copilot-chat-subthreading-helpers'
import type {CopilotChatState} from '@github-ui/copilot-chat-state/copilot-chat-reducer'
import {type CopilotChatMessage, NullMessageId} from '@github-ui/copilot-chat-types'
import {useMemo} from 'react'

import {groupChatMessages} from '../components/ChatMessagesGroup'
import {fuseAssistantMessages} from '../utils/tool-grouping'

type GroupedMessagesState = Pick<
  CopilotChatState,
  'streamingMessage' | 'isWaitingOnCopilot' | 'messages' | 'selectedThreadID' | 'pendingFirstMessage'
>

export function useGroupedMessages(state: GroupedMessagesState) {
  const {
    streamingMessage,
    isWaitingOnCopilot,
    messages: completedMessages,
    selectedThreadID,
    pendingFirstMessage,
  } = state

  const messages = useMemo(() => {
    const nullMessage: CopilotChatMessage = {
      id: NullMessageId,
      role: 'assistant',
      content: '',
      createdAt: '',
      threadID: selectedThreadID || '',
      references: [],
      clientSide: true,
      messageIndex: -1,
    }

    const activeMessages = getActiveMessages(completedMessages, pendingFirstMessage)

    const withStreaming = isWaitingOnCopilot ? [...activeMessages, streamingMessage ?? nullMessage] : activeMessages

    return fuseAssistantMessages(withStreaming)
  }, [selectedThreadID, completedMessages, pendingFirstMessage, isWaitingOnCopilot, streamingMessage])

  // Keep the same groups and array reference when `messages` hasn't changed so memoized messages
  // can skip re-rendering while tokens stream.
  const groupedMessages = useMemo(() => groupChatMessages(messages), [messages])

  return {messages, groupedMessages}
}
